import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Card, CardContent } from "./ui/card";
import { apiClient } from "../App";
import { Trophy, TrendingUp, Users, MapPin, Calendar, Loader2 } from "lucide-react";

const SessionMatchesModal = ({ open, onOpenChange, session }) => {
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && session) {
      fetchSessionMatches();
    }
  }, [open, session]);

  const fetchSessionMatches = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/sessions/${session.id}/matches`);
      const matchData = res.data || [];
      setMatches(matchData);
      
      // Calculate session-specific stats
      calculateStats(matchData);
    } catch (error) {
      console.error("Failed to fetch session matches", error);
      setMatches([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (matchData) => {
    if (!matchData || matchData.length === 0) {
      setStats(null);
      return;
    }

    const playerStats = {};
    const pairStats = {};

    matchData.forEach(match => {
      const winnerTeam = match.winner === "team_a" ? match.team_a : match.team_b;
      const winnerNames = match.winner === "team_a" ? match.team_a_names : match.team_b_names;
      const loserTeam = match.winner === "team_a" ? match.team_b : match.team_a;
      const loserNames = match.winner === "team_a" ? match.team_b_names : match.team_a_names;

      // Update individual stats - winners
      winnerTeam.forEach((uid, idx) => {
        if (!playerStats[uid]) {
          playerStats[uid] = { wins: 0, losses: 0, name: winnerNames[idx] };
        }
        playerStats[uid].wins += 1;
        playerStats[uid].name = winnerNames[idx];
      });

      // Update individual stats - losers
      loserTeam.forEach((uid, idx) => {
        if (!playerStats[uid]) {
          playerStats[uid] = { wins: 0, losses: 0, name: loserNames[idx] };
        }
        playerStats[uid].losses += 1;
        playerStats[uid].name = loserNames[idx];
      });

      // Update pair stats (for doubles)
      if (winnerTeam.length === 2) {
        const pairKey = [...winnerTeam].sort().join("-");
        const pairName = winnerNames.join(" & ");
        if (!pairStats[pairKey]) {
          pairStats[pairKey] = { wins: 0, losses: 0, name: pairName };
        }
        pairStats[pairKey].wins += 1;
      }

      if (loserTeam.length === 2) {
        const pairKey = [...loserTeam].sort().join("-");
        const pairName = loserNames.join(" & ");
        if (!pairStats[pairKey]) {
          pairStats[pairKey] = { wins: 0, losses: 0, name: pairName };
        }
        pairStats[pairKey].losses += 1;
      }
    });

    // Calculate top performers
    const topWinners = Object.values(playerStats)
      .map(p => ({ name: p.name, wins: p.wins, matches: p.wins + p.losses }))
      .sort((a, b) => b.wins - a.wins)
      .slice(0, 3);

    const topWinRate = Object.values(playerStats)
      .map(p => ({
        name: p.name,
        winPct: p.wins + p.losses > 0 ? Math.round((p.wins / (p.wins + p.losses)) * 100) : 0,
        matches: p.wins + p.losses
      }))
      .filter(p => p.matches >= 1)
      .sort((a, b) => b.winPct - a.winPct)
      .slice(0, 3);

    const topPairs = Object.values(pairStats)
      .map(p => ({ name: p.name, wins: p.wins, matches: p.wins + p.losses }))
      .sort((a, b) => b.wins - a.wins)
      .slice(0, 3);

    setStats({
      topWinners,
      topWinRate,
      topPairs,
      totalMatches: matchData.length
    });
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-[#064E3B]" />
            Session Match Results
          </DialogTitle>
          {session && (
            <div className="flex flex-wrap gap-3 text-sm text-slate-500 mt-1">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {session.turf_location_name}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(session.date)}
              </span>
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" />
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No match scores recorded for this session yet.</p>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Session Stats */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Top Winners */}
                <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 to-amber-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Trophy className="w-4 h-4 text-yellow-600" />
                      <span className="font-semibold text-yellow-800 text-sm">Most Wins</span>
                    </div>
                    <div className="space-y-2">
                      {stats.topWinners.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                              idx === 1 ? 'bg-slate-300 text-slate-700' :
                              'bg-amber-600 text-white'
                            }`}>
                              {idx + 1}
                            </span>
                            <span className="text-sm text-slate-700 truncate max-w-[100px]">{p.name}</span>
                          </div>
                          <span className="font-bold text-yellow-700">{p.wins}</span>
                        </div>
                      ))}
                      {stats.topWinners.length === 0 && (
                        <p className="text-xs text-slate-400">No data</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Best Win Rate */}
                <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-blue-800 text-sm">Best Win %</span>
                    </div>
                    <div className="space-y-2">
                      {stats.topWinRate.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-blue-400 text-white' :
                              idx === 1 ? 'bg-slate-300 text-slate-700' :
                              'bg-blue-600 text-white'
                            }`}>
                              {idx + 1}
                            </span>
                            <span className="text-sm text-slate-700 truncate max-w-[100px]">{p.name}</span>
                          </div>
                          <span className="font-bold text-blue-700">{p.winPct}%</span>
                        </div>
                      ))}
                      {stats.topWinRate.length === 0 && (
                        <p className="text-xs text-slate-400">No data</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Best Pairs */}
                <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-green-800 text-sm">Best Pairs</span>
                    </div>
                    <div className="space-y-2">
                      {stats.topPairs.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-green-400 text-green-900' :
                              idx === 1 ? 'bg-slate-300 text-slate-700' :
                              'bg-green-600 text-white'
                            }`}>
                              {idx + 1}
                            </span>
                            <span className="text-sm text-slate-700 truncate max-w-[100px]" title={p.name}>{p.name}</span>
                          </div>
                          <span className="font-bold text-green-700">{p.wins}</span>
                        </div>
                      ))}
                      {stats.topPairs.length === 0 && (
                        <p className="text-xs text-slate-400">No pairs data</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* All Matches */}
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                📋 All Matches ({matches.length})
              </h3>
              <div className="space-y-2">
                {matches.map((match, idx) => {
                  const isTeamAWinner = match.winner === "team_a";
                  return (
                    <div
                      key={match.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-slate-400 text-sm font-medium w-6">#{idx + 1}</span>
                        <div className={`flex-1 text-sm ${isTeamAWinner ? 'font-bold text-green-700' : 'text-slate-600'}`}>
                          {match.team_a_names.join(" & ")}
                          {isTeamAWinner && <Trophy className="w-3 h-3 inline ml-1 text-yellow-500" />}
                        </div>
                      </div>
                      <div className="px-4 py-1 bg-white rounded-lg border font-bold text-slate-800 text-sm">
                        {match.score_a} - {match.score_b}
                      </div>
                      <div className={`flex-1 text-sm text-right ${!isTeamAWinner ? 'font-bold text-green-700' : 'text-slate-600'}`}>
                        {!isTeamAWinner && <Trophy className="w-3 h-3 inline mr-1 text-yellow-500" />}
                        {match.team_b_names.join(" & ")}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SessionMatchesModal;
